/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { Query_Keys } from "../../../lib/queryKeys";
import { mergeQueryKeys, getBaseQueryKey } from "../utils/queryKeyHelpers";
import {
  billingReportRefreshKeys,
  checkInComplianceRefreshKeys,
  subGroupDependentRefreshKeys,
} from "./sharedRefreshKeys";

// A restaurant's sub group linkage can change on create/update/delete, which changes
// which restaurants match a given sub group filter, so the Employees, Network Guests,
// Employee Perq Card Allocation, Employee Discounts and Named User Discounts report
// queries must be invalidated alongside the restaurant caches below.
const restaurantSubGroupRefreshKeys = subGroupDependentRefreshKeys;

const restaurantUpdateRefreshKeys = mergeQueryKeys(
  getBaseQueryKey(Query_Keys.restaurants),
  getBaseQueryKey(Query_Keys.restaurants_dropdown),
  getBaseQueryKey(Query_Keys.show_restaurant),
  getBaseQueryKey(Query_Keys.toast_credential_logs),
  getBaseQueryKey(Query_Keys.audience_per_restaurant),
  getBaseQueryKey(Query_Keys.create_restaurant_dropdown),
  getBaseQueryKey(Query_Keys.associated_guest_restaurants),
  getBaseQueryKey(Query_Keys.top_restaurants),
  getBaseQueryKey(Query_Keys.back_office_restaurants_dropdown),
  getBaseQueryKey(Query_Keys.configured_restaurants),
  getBaseQueryKey(Query_Keys.import_logs),
  getBaseQueryKey(Query_Keys.discount_report_summary),
  getBaseQueryKey(Query_Keys.discount_report_breakdown),
  getBaseQueryKey(Query_Keys.discount_reports),
  getBaseQueryKey(Query_Keys.labor_reports),
  getBaseQueryKey(Query_Keys.labor_report_summary),
  checkInComplianceRefreshKeys,
  // Restaurants are the Billing Report's rows: a rename, move or delete changes them and their orders.
  billingReportRefreshKeys
);

export const restaurantMutationKeyGroups = {
  delete: mergeQueryKeys(
    restaurantUpdateRefreshKeys,
    restaurantSubGroupRefreshKeys
  ),
  updateCoreDetails: mergeQueryKeys(
    restaurantUpdateRefreshKeys,
    restaurantSubGroupRefreshKeys
  ),
  updateMedia: restaurantUpdateRefreshKeys,
  updateLocation: mergeQueryKeys(
    restaurantUpdateRefreshKeys,
    getBaseQueryKey(Query_Keys.tip_compliance)
  ),
  updateAdditionalDetails: mergeQueryKeys(
    restaurantUpdateRefreshKeys,
    getBaseQueryKey(Query_Keys.tip_compliance),
    restaurantSubGroupRefreshKeys
  ),
  toggleVisibility: restaurantUpdateRefreshKeys,
  updateTranslations: restaurantUpdateRefreshKeys,
  create: mergeQueryKeys(
    getBaseQueryKey(Query_Keys.restaurants),
    getBaseQueryKey(Query_Keys.restaurants_dropdown),
    getBaseQueryKey(Query_Keys.audience_per_restaurant),
    getBaseQueryKey(Query_Keys.create_restaurant_dropdown),
    getBaseQueryKey(Query_Keys.back_office_restaurants_dropdown),
    restaurantSubGroupRefreshKeys,
    // A new restaurant gets a zero-activity row and counts toward the Restaurants card.
    checkInComplianceRefreshKeys,
    // ...and a $0 Billing Report row when its group has a cutoff date.
    billingReportRefreshKeys
  ),
};

export const restaurantRemoveKeyGroups = {
  invokeJobs: [getBaseQueryKey(Query_Keys.restaurants)],
  revokeJobs: [getBaseQueryKey(Query_Keys.restaurants)],
};
